import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Card, Row, Col, Statistic, Input, Select, Button, Space,
  DatePicker, message, Tabs, Modal, Alert, Form, InputNumber, Tooltip, Badge, Typography
} from 'antd';
import {
  DollarOutlined, SearchOutlined, DownloadOutlined, CheckCircleOutlined,
  SyncOutlined, FlagOutlined, EditOutlined, AuditOutlined, ExclamationCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import API from '../api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

const entryTypeColour = (t: string) => ({
  PROVIDER_CREDIT: 'green', TIP: 'cyan', PAYOUT_FAILED: 'volcano',
  PAYOUT_INITIATED: 'orange', PAYOUT_COMPLETED: 'blue',
  REFUND_DEBIT: 'red', PLATFORM_FEE: 'geekblue',
  TRUST_SAFETY_FEE: 'purple', MANUAL_ADJUSTMENT: 'gold',
}[t] || 'default');

// ══════════════════════════════════════════════════════════════════════════
// L01 — Unified Transaction Ledger
// ══════════════════════════════════════════════════════════════════════════
const UnifiedLedger: React.FC = () => {
  const [entries, setEntries] = useState<any[]>([]);
  const [totalGross, setTotalGross] = useState(0);
  const [totalFee, setTotalFee]     = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(1);
  const [providerName, setProviderName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [entryType, setEntryType]     = useState('');
  const [dateRange, setDateRange]     = useState<any>(null);
  const [flaggedIds, setFlaggedIds]   = useState<Set<string>>(new Set());
  const [annotateEntry, setAnnotateEntry] = useState<any>(null);
  const [annotateNote, setAnnotateNote]   = useState('');
  const [annotateFlag, setAnnotateFlag]   = useState(false);

  const fetchFlagged = useCallback(() => {
    API.get('/ledger/flagged')
      .then(r => setFlaggedIds(new Set(r.data.map((x: any) => x.ledgerEntryId))))
      .catch(() => {});
  }, []);

  const fetchLedger = useCallback((p = 1) => {
    setLoading(true);
    const params: any = { page: p - 1, size: 50 };
    if (providerName)    params.providerName  = providerName;
    if (customerName)    params.customerName  = customerName;
    if (paymentMethod)   params.paymentMethod = paymentMethod;
    if (entryType)       params.entryType     = entryType;
    if (dateRange?.[0])  params.from = dayjs(dateRange[0]).format('YYYY-MM-DD');
    if (dateRange?.[1])  params.to   = dayjs(dateRange[1]).format('YYYY-MM-DD');

    API.get('/payments/ledger', { params })
      .then(r => {
        setEntries(r.data.entries?.content || []);
        setTotalCount(r.data.entries?.totalElements || 0);
        setTotalGross(Number(r.data.totalGrossVolume || 0));
        setTotalFee(Number(r.data.totalPlatformFee   || 0));
      })
      .catch(() => message.error('Failed to load ledger'))
      .finally(() => setLoading(false));
  }, [providerName, customerName, paymentMethod, entryType, dateRange]);

  useEffect(() => { fetchLedger(1); fetchFlagged(); }, []);

  const handleAnnotateSave = (entryId: string) => {
    API.post(`/ledger/${entryId}/annotate`, { note: annotateNote, isFlagged: annotateFlag })
      .then(() => { message.success('Annotation saved'); setAnnotateEntry(null); fetchFlagged(); })
      .catch(() => message.error('Failed to save annotation'));
  };

  const handleExport = () => {
    const params: any = {};
    if (providerName)   params.providerName  = providerName;
    if (customerName)   params.customerName  = customerName;
    if (paymentMethod)  params.paymentMethod = paymentMethod;
    if (entryType)      params.entryType     = entryType;
    if (dateRange?.[0]) params.from = dayjs(dateRange[0]).format('YYYY-MM-DD');
    if (dateRange?.[1]) params.to   = dayjs(dateRange[1]).format('YYYY-MM-DD');
    window.open(API.defaults.baseURL + '/payments/ledger/export?' + new URLSearchParams(params), '_blank');
  };

  const columns = [
    {
      title: '', width: 28,
      render: (_: any, r: any) => flaggedIds.has(r.id)
        ? <Tooltip title="Flagged entry"><FlagOutlined style={{ color: '#f5222d' }} /></Tooltip>
        : null
    },
    { title: 'ID', dataIndex: 'id', width: 100, render: (v: string) => <Text code ellipsis style={{ width: 90, display: 'block' }}>{v?.substring(0, 12)}...</Text> },
    { title: 'Provider', dataIndex: 'providerId', width: 80 },
    { title: 'Type', dataIndex: 'entryType', render: (v: string) => <Tag color={entryTypeColour(v)}>{v}</Tag> },
    { title: 'Method', dataIndex: 'paymentMethod', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Gross', dataIndex: 'grossAmount', render: (v: number) => `R${Number(v).toFixed(2)}` },
    {
      title: 'Net', dataIndex: 'netAmount',
      render: (v: number) => (
        <span style={{ color: v >= 0 ? '#16a34a' : '#dc2626' }}>
          R{Number(v).toFixed(2)}
        </span>
      )
    },
    { title: 'Balance', dataIndex: 'balanceAfter', render: (v: number) => `R${Number(v).toFixed(2)}` },
    { title: 'Source', dataIndex: 'sourceSystem', render: (v: string) => <Tag color={v === 'MANUAL_ADJUSTMENT' ? 'gold' : undefined}>{v}</Tag> },
    { title: 'Date', dataIndex: 'createdAt', render: (v: string) => new Date(v).toLocaleString() },
    {
      title: '',
      render: (_: any, r: any) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => {
          setAnnotateEntry(r); setAnnotateNote(''); setAnnotateFlag(false);
          API.get(`/ledger/${r.id}/annotation`).then(res => {
            setAnnotateNote(res.data.note || '');
            setAnnotateFlag(res.data.flagged || false);
          }).catch(() => {});
        }} />
      )
    }
  ];

  return (
    <div>
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col span={8}><Card><Statistic title="Gross Volume (filtered)" value={totalGross} prefix="R" precision={2} valueStyle={{ color: '#16a34a' }} /></Card></Col>
        <Col span={8}><Card><Statistic title="Platform Fee (filtered)" value={totalFee} prefix="R" precision={2} valueStyle={{ color: '#1e40af' }} /></Card></Col>
        <Col span={8}><Card><Statistic title="Entries (filtered)" value={totalCount} /></Card></Col>
      </Row>

      <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
        <Space wrap>
          <Input placeholder="Provider name" value={providerName} onChange={e => setProviderName(e.target.value)} style={{ width: 150 }} prefix={<SearchOutlined />} />
          <Input placeholder="Customer name" value={customerName} onChange={e => setCustomerName(e.target.value)} style={{ width: 150 }} prefix={<SearchOutlined />} />
          <Select value={paymentMethod} onChange={setPaymentMethod} style={{ width: 110 }} options={[
            { value: '', label: 'All Methods' },
            { value: 'NFC', label: 'NFC' }, { value: 'QR_POS', label: 'QR/POS' },
            { value: 'ESCROW', label: 'Escrow' }, { value: 'CARD', label: 'Card' }
          ]} />
          <Select value={entryType} onChange={setEntryType} style={{ width: 155 }} options={[
            { value: '', label: 'All Types' },
            { value: 'PROVIDER_CREDIT', label: 'Credit' },
            { value: 'PAYOUT_INITIATED', label: 'Payout Init' },
            { value: 'PAYOUT_COMPLETED', label: 'Payout Done' },
            { value: 'REFUND_DEBIT', label: 'Refund' },
            { value: 'PLATFORM_FEE', label: 'Platform Fee' },
            { value: 'MANUAL_ADJUSTMENT', label: 'Manual Adj.' }
          ]} />
          <RangePicker onChange={v => setDateRange(v)} />
          <Button type="primary" onClick={() => { setPage(1); fetchLedger(1); }}>Search</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>Export CSV</Button>
          <Button icon={<SyncOutlined />} onClick={() => fetchLedger(page)}>Refresh</Button>
        </Space>
      </Card>

      <Table
        dataSource={entries} columns={columns} rowKey="id" size="small" loading={loading}
        rowClassName={(r: any) => flaggedIds.has(r.id) ? 'ant-table-row-selected' : ''}
        pagination={{ current: page, pageSize: 50, total: totalCount, onChange: p => { setPage(p); fetchLedger(p); } }}
      />

      <Modal
        title={`Annotate Entry`} open={!!annotateEntry}
        onOk={() => handleAnnotateSave(annotateEntry?.id)} onCancel={() => setAnnotateEntry(null)}
        okText="Save"
      >
        <Form layout="vertical">
          <Form.Item label="Note (max 500 characters)">
            <TextArea rows={4} maxLength={500} showCount value={annotateNote} onChange={e => setAnnotateNote(e.target.value)} />
          </Form.Item>
          <Form.Item label="Flag this entry">
            <Button type={annotateFlag ? 'primary' : 'default'} danger={annotateFlag}
              icon={<FlagOutlined />} onClick={() => setAnnotateFlag(!annotateFlag)}>
              {annotateFlag ? 'Flagged — click to unflag' : 'Not flagged'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// L04 — Payout Approval Queue (Manual EFT)
// ══════════════════════════════════════════════════════════════════════════
const PayoutQueue: React.FC = () => {
  const [pending, setPending]     = useState<any[]>([]);
  const [completed, setCompleted] = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [confirmModal, setConfirmModal] = useState(false);
  const [rejectModal, setRejectModal]   = useState(false);
  const [selected, setSelected]         = useState<any>(null);
  const [confirmForm] = Form.useForm();
  const [rejectForm]  = Form.useForm();

  const fetchPayouts = useCallback(() => {
    setLoading(true);
    Promise.all([API.get('/payouts/pending'), API.get('/payouts/completed')])
      .then(([p, c]) => { setPending(p.data || []); setCompleted(c.data || []); })
      .catch(() => message.error('Failed to load payouts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPayouts(); }, []);

  const submitConfirm = async () => {
    try {
      const values = await confirmForm.validateFields();
      await API.post(`/payouts/${selected.id}/approve`, {
        reference:   values.reference,
        notes:       '',
        processedBy: 'admin',
      });
      message.success('Payout approved — provider notified');
      setConfirmModal(false);
      fetchPayouts();
    } catch (e: any) {
      if (e?.response?.data?.error) message.error(e.response.data.error);
    }
  };

  const submitReject = async () => {
    try {
      const values = await rejectForm.validateFields();
      await API.post(`/payouts/${selected.id}/reject`, {
        reason:      values.reason,
        processedBy: 'admin',
      });
      message.success('Payout rejected — balance restored');
      setRejectModal(false);
      fetchPayouts();
    } catch (e: any) {
      if (e?.response?.data?.error) message.error(e.response.data.error);
    }
  };

  const maskAccount = (n: string) => (!n || n.length < 4) ? '••••' : `${n.slice(0, -4).replace(/./g, '•')}${n.slice(-4)}`;

  const pendingCols = [
    { title: 'Provider', dataIndex: 'providerName' },
    { title: 'Bank', dataIndex: 'bankName' },
    { title: 'Account', dataIndex: 'accountNumber', render: (v: string) => maskAccount(v) },
    { title: 'Amount', dataIndex: 'amount', render: (v: number) => `R${Number(v).toFixed(2)}` },
    { title: 'Requested', dataIndex: 'requestedAt', render: (v: string) => new Date(v).toLocaleString() },
    {
      title: 'Waiting',
      render: (_: any, r: any) => {
        const days = dayjs().diff(dayjs(r.requestedAt), 'day');
        return <Badge count={days === 0 ? 'Today' : `${days}d`} style={{ backgroundColor: days > 2 ? '#f5222d' : '#52c41a' }} />;
      }
    },
    {
      title: 'Action',
      render: (_: any, r: any) => (
        <Space>
          <Button type="primary" size="small" icon={<CheckCircleOutlined />} onClick={() => {
            setSelected(r);
            confirmForm.resetFields();
            setConfirmModal(true);
          }}>Mark as Paid</Button>
          <Button danger size="small" onClick={() => { setSelected(r); rejectForm.resetFields(); setRejectModal(true); }}>Reject</Button>
        </Space>
      )
    }
  ];

  const completedCols = [
    { title: 'Provider', dataIndex: 'providerName' },
    { title: 'Amount', dataIndex: 'amount', render: (v: number) => `R${Number(v).toFixed(2)}` },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'COMPLETED' ? 'green' : 'red'}>{v}</Tag> },
    { title: 'EFT Ref', dataIndex: 'reference', render: (v: string) => v || '—' },
    { title: 'Processed', dataIndex: 'processedAt', render: (v: string) => v ? new Date(v).toLocaleString() : '—' },
    { title: 'Notes', dataIndex: 'adminNotes', render: (v: string) => v || '—' }
  ];

  return (
    <div>
      <Alert
        message="Manual Payout Mode"
        description="Process each withdrawal via your bank EFT. Click 'Mark as Paid' after completing the transfer and enter the EFT reference. For the full payout management view, use the dedicated Payout Requests page."
        type="info" showIcon style={{ marginBottom: 16 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3>Pending Withdrawals (≥ R150)</h3>
        <Button icon={<SyncOutlined />} onClick={fetchPayouts}>Refresh</Button>
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab={`Pending (${pending.length})`} key="pending">
          <Table dataSource={pending} columns={pendingCols} rowKey="id" loading={loading} size="middle" />
        </Tabs.TabPane>
        <Tabs.TabPane tab={`History (${completed.length})`} key="completed">
          <Table dataSource={completed} columns={completedCols} rowKey="id" loading={loading} size="middle" />
        </Tabs.TabPane>
      </Tabs>

      <Modal title="Confirm Manual EFT" open={confirmModal} onOk={submitConfirm} onCancel={() => setConfirmModal(false)}>
        {selected && (
          <p style={{ marginBottom: 16 }}>
            Approving <strong>R{Number(selected.amount).toFixed(2)}</strong> payout for <strong>{selected.providerName}</strong>
            {' '}to {selected.bankName} ••••{selected.accountNumber?.slice(-4)}.
          </p>
        )}
        <Form form={confirmForm} layout="vertical">
          <Form.Item name="reference" label="EFT Reference Number" rules={[{ required: true, message: 'Reference is required' }]}>
            <Input placeholder="e.g. GT20260417123456" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Reject Payout" open={rejectModal} onOk={submitReject} onCancel={() => setRejectModal(false)} okButtonProps={{ danger: true }}>
        <Form form={rejectForm} layout="vertical">
          <Form.Item name="reason" label="Rejection Reason" rules={[{ required: true }]}>
            <Input.TextArea placeholder="Enter reason — provider's balance will be restored" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// L06 — Reconciliation Tool
// ══════════════════════════════════════════════════════════════════════════
const ReconciliationTool: React.FC = () => {
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState<any>(null);
  const [dateRange, setDateRange] = useState<any>(null);

  const runRecon = () => {
    if (!dateRange?.[0] || !dateRange?.[1]) { message.warning('Select a date range first'); return; }
    setRunning(true);
    API.post('/reconciliation/run', {
      from: dayjs(dateRange[0]).format('YYYY-MM-DD'),
      to:   dayjs(dateRange[1]).format('YYYY-MM-DD')
    }).then(r => setResult(r.data))
    .catch(() => message.error('Reconciliation failed'))
    .finally(() => setRunning(false));
  };

  const discCols = [
    { title: 'Type', dataIndex: 'type', render: (v: string) => <Tag color={v === 'NFC' ? 'blue' : 'purple'}>{v}</Tag> },
    { title: 'Reference ID', dataIndex: 'referenceId' },
    { title: 'Expected Amount', dataIndex: 'expectedAmount', render: (v: number) => v != null ? `R${Number(v).toFixed(2)}` : '—' },
    { title: 'Status', render: () => <Tag color="red">MISSING</Tag> },
    { title: 'Description', dataIndex: 'description', ellipsis: true }
  ];

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Read-only reconciliation. Discrepancies must be fixed manually via the Manual Adjustment tab." />
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <RangePicker onChange={v => setDateRange(v)} />
          <Button type="primary" loading={running} icon={<AuditOutlined />} onClick={runRecon}>Run Reconciliation</Button>
        </Space>
      </Card>
      {result && (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col span={6}><Card><Statistic title="Switche Sessions Checked" value={result.switcheSessionsChecked} /></Card></Col>
            <Col span={6}><Card><Statistic title="Switche Reconciled" value={result.switcheReconciled} valueStyle={{ color: '#52c41a' }} /></Card></Col>
            <Col span={6}><Card><Statistic title="NFC Invoices Checked" value={result.nfcInvoicesChecked} /></Card></Col>
            <Col span={6}><Card><Statistic title="Discrepancies" value={result.discrepancyCount} valueStyle={{ color: result.discrepancyCount > 0 ? '#f5222d' : '#52c41a' }} /></Card></Col>
          </Row>
          {result.note && <Alert type="warning" message={result.note} showIcon icon={<WarningOutlined />} style={{ marginBottom: 12 }} />}
          <Card title={`Discrepancies (${result.discrepancyCount})`}
            extra={<Button size="small" icon={<DownloadOutlined />}
              onClick={() => window.open(API.defaults.baseURL + '/reconciliation/export?runId=' + result.runId, '_blank')}>
              Export CSV
            </Button>}>
            <Table dataSource={result.discrepancies || []} columns={discCols} rowKey="referenceId" size="small" />
          </Card>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// L07 — Manual Ledger Adjustment (Two-Officer)
// ══════════════════════════════════════════════════════════════════════════
const ManualAdjustment: React.FC = () => {
  const [form] = Form.useForm();
  const [pending, setPending]     = useState<any[]>([]);
  const [loading, setLoading]     = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = useCallback(() => {
    setLoading(true);
    API.get('/ledger/adjustment/pending')
      .then(r => setPending(r.data || []))
      .catch(() => message.error('Failed to load pending adjustments'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPending(); }, []);

  const handleInitiate = (values: any) => {
    setSubmitting(true);
    API.post('/ledger/adjustment/initiate', values)
      .then(r => {
        message.success(`Adjustment initiated. Request ID: ${r.data.adjustmentRequestId}. A second officer must confirm.`);
        form.resetFields();
        fetchPending();
      })
      .catch(err => message.error(err.response?.data?.error || 'Failed to initiate adjustment'))
      .finally(() => setSubmitting(false));
  };

  const handleConfirm = (requestId: string) => {
    Modal.confirm({
      title: 'Confirm this adjustment?',
      icon: <ExclamationCircleOutlined />,
      content: 'This will permanently write an entry to the provider ledger. You cannot be the same officer who initiated.',
      okText: 'Confirm', okType: 'danger',
      onOk: () => API.post(`/ledger/adjustment/${requestId}/confirm`)
        .then(() => { message.success('Adjustment confirmed — ledger entry written'); fetchPending(); })
        .catch(err => message.error(err.response?.data?.error || 'Failed to confirm'))
    });
  };

  const handleReject = (requestId: string) => {
    API.post(`/ledger/adjustment/${requestId}/reject`)
      .then(() => { message.success('Adjustment rejected'); fetchPending(); })
      .catch(() => message.error('Failed to reject'));
  };

  const pendingCols = [
    { title: 'Request ID', dataIndex: 'id', render: (v: string) => <Text code>{v?.substring(0, 12)}...</Text> },
    { title: 'Provider ID', dataIndex: 'providerId' },
    { title: 'Type', dataIndex: 'entryType', render: (v: string) => <Tag color={entryTypeColour(v)}>{v}</Tag> },
    { title: 'Amount', dataIndex: 'amount', render: (v: number) => `R${Number(v).toFixed(2)}` },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: 'Initiated By', dataIndex: 'initiatedByAdminId' },
    {
      title: 'Actions',
      render: (_: any, r: any) => (
        <Space>
          <Tooltip title="A different officer must confirm">
            <Button size="small" type="primary" onClick={() => handleConfirm(r.id)}>Confirm</Button>
          </Tooltip>
          <Button size="small" danger onClick={() => handleReject(r.id)}>Reject</Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Alert type="warning" showIcon style={{ marginBottom: 16 }}
        message="Two-officer rule enforced. The officer who initiates cannot confirm. Backend returns 403 if the same officer tries to confirm." />
      <Row gutter={24}>
        <Col span={10}>
          <Card title="Initiate Adjustment">
            <Form form={form} layout="vertical" onFinish={handleInitiate}>
              <Form.Item name="providerId" label="Provider ID" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
              <Form.Item name="entryType" label="Entry Type" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'PROVIDER_CREDIT', label: 'Provider Credit (+)' },
                  { value: 'REFUND_DEBIT', label: 'Refund Debit (-)' }
                ]} />
              </Form.Item>
              <Form.Item name="amount" label="Amount (ZAR)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0.01} precision={2} prefix="R" />
              </Form.Item>
              <Form.Item name="reason" label="Reason" rules={[{ required: true, min: 5 }]}>
                <Input.TextArea rows={3} placeholder="e.g. Dispute resolved — provider underpaid on booking #123" />
              </Form.Item>
              <Form.Item name="linkedBookingId" label="Linked Booking ID (optional)">
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} block>Initiate Adjustment</Button>
            </Form>
          </Card>
        </Col>
        <Col span={14}>
          <Card title={`Pending Confirmations (${pending.length})`}
            extra={<Button size="small" icon={<SyncOutlined />} onClick={fetchPending}>Refresh</Button>}>
            <Table dataSource={pending} columns={pendingCols} rowKey="id" loading={loading} size="small" />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// Main Payments Page
// ══════════════════════════════════════════════════════════════════════════
export default function Payments() {
  const [revSummary, setRevSummary] = useState<any>({});

  useEffect(() => {
    API.get('/payments/ledger/summary').then(r => setRevSummary(r.data)).catch(() => {
      API.get('/payments/summary').then(r => setRevSummary({
        totalGross: r.data.grossRevenue,
        totalPlatformFee: r.data.platformCommission
      })).catch(() => {});
    });
  }, []);

  return (
    <div style={{ padding: 24 }}>
      {/* L02 — Revenue Dashboard Header */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Gross Transaction Volume" value={Number(revSummary.totalGross || 0)}
              prefix="R" precision={2} valueStyle={{ color: '#16a34a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Platform Fee Earned" value={Number(revSummary.totalPlatformFee || 0)}
              prefix="R" precision={2} valueStyle={{ color: '#1e40af' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Net Commission" value={Number(revSummary.totalPlatformFee || 0)}
              prefix="R" precision={2} valueStyle={{ color: '#d97706' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Period" value={revSummary.from === 'all-time' ? 'All Time' : 'Filtered'} />
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="ledger">
        <Tabs.TabPane tab="Transaction Ledger (L01)" key="ledger">
          <UnifiedLedger />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Payout Queue (L04)" key="payouts">
          <Card><PayoutQueue /></Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Reconciliation (L06)" key="reconciliation">
          <Card><ReconciliationTool /></Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Manual Adjustment (L07)" key="adjustment">
          <ManualAdjustment />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
